<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ChatMessage from "@/views/chats/components/messages/Message.vue";
import MessageMenu from "@/views/chats/components/messages/MessageMenu.vue";
import { API } from "@/api/api";
import { useChatStore } from "@/store/chat.store";
import { useGalleryStore } from "@/store/gallery.store";
import { MessageType } from "@/constants/chat";
import type { MessageDto } from "@/api/chat/dto/message.dto";
import type { ChatStateThread } from "@/store/chat.store";
import "vue-cool-lightbox/dist/vue-cool-lightbox.min.css";

const SHOW_DOWN_BUTTON_OFFSET = 350;
const MESSAGES_PAGE_SIZE = 50;

const props = defineProps<{
  thread: ChatStateThread;
}>();

defineOptions({ name: "MessagesList" });

const { t } = useI18n();
const chatStore = useChatStore();
const galleryStore = useGalleryStore();
const api = new API();

const chatContainer = ref<HTMLElement | null>(null);
const messagesList = ref<HTMLElement | null>(null);

const loading = ref(false);
const scrollTop = ref(0);
const doNotDownloadNewMessagesOnScroll = ref(false);
const canLoadMore = ref(true);
const oldScrollBottom = ref(0);
const initialized = ref(false);
const showDownButton = ref(false);
const lastMessage = ref<MessageDto | null>(null);
const newMessagesCount = ref(0);
const showMenu = ref(false);
const menuTop = ref(0);
const menuLeft = ref(0);
const selectedMessage = ref<MessageDto | null>(null);

const messages = computed<MessageDto[]>(() => {
  const thread = chatStore.getThread(props.thread.id);

  return thread ? thread.messages : [];
});

const activeMediaMessages = computed(() =>
  messages.value
    .filter((message) => message.type === MessageType.Image && !message.deletedBy)
    .map((message) => ({ id: message.id, url: message.url })),
);

const showBottomLine = computed(() => oldScrollBottom.value > 32);

function clickOnMediaMessage(messageId: number) {
  galleryStore.setMedia(activeMediaMessages.value);
  galleryStore.setMediaId(messageId);
}

function getChatContainerScrollBottom(): number {
  if (!chatContainer.value) {
    return 0;
  }

  return chatContainer.value.scrollHeight - chatContainer.value.scrollTop - chatContainer.value.clientHeight;
}

function handleChatScroll(event: Event) {
  oldScrollBottom.value = getChatContainerScrollBottom();
  scrollTop.value = (event.target as HTMLElement).scrollTop;
  showDownButton.value = getChatContainerScrollBottom() > SHOW_DOWN_BUTTON_OFFSET;

  if (!showDownButton.value) {
    newMessagesCount.value = 0;
  }
}

function allowDownloadMessagesOnScroll() {
  setTimeout(() => (doNotDownloadNewMessagesOnScroll.value = false), 50);
}

function restoreScroll() {
  if (!chatContainer.value) {
    return;
  }

  doNotDownloadNewMessagesOnScroll.value = true;
  chatContainer.value.scrollTop =
    chatContainer.value.scrollHeight - oldScrollBottom.value - chatContainer.value.clientHeight;
  allowDownloadMessagesOnScroll();
}

async function loadNextMessages() {
  if (loading.value || !canLoadMore.value) {
    return;
  }

  loading.value = true;

  try {
    const response = await api.chat.getThreadMessages(props.thread.id, MESSAGES_PAGE_SIZE, messages.value.length);

    if (!response.data || response.data.length === 0) {
      canLoadMore.value = false;

      return;
    }

    if (response.data[0].threadId !== props.thread.id) {
      return;
    }

    oldScrollBottom.value = getChatContainerScrollBottom();
    chatStore.addOlderMessages({ threadId: props.thread.id, messages: response.data });

    await nextTick();
    restoreScroll();
  } catch (error) {
    console.error("Can not load thread messages", error);
  } finally {
    loading.value = false;
  }
}

function scrollToLastMessage(behavior: ScrollBehavior = "smooth") {
  messagesList.value?.lastElementChild?.scrollIntoView({ block: "end", behavior: behavior });
}

function scrollBottom() {
  if (!chatContainer.value) {
    return;
  }

  doNotDownloadNewMessagesOnScroll.value = true;
  chatContainer.value.scrollTop = chatContainer.value.scrollHeight;

  nextTick(() => {
    if (!chatContainer.value) {
      return;
    }

    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
    allowDownloadMessagesOnScroll();
  });
}

function setLoading() {
  loading.value = true;
  messagesList.value?.classList.add("hidden");

  if (chatContainer.value) {
    chatContainer.value.style.overflowY = "hidden";
  }
}

function removeLoading() {
  nextTick(() => {
    messagesList.value?.classList.remove("hidden");

    if (chatContainer.value) {
      chatContainer.value.style.overflowY = "auto";
    }

    loading.value = false;
  });
}

function handleNewMessage() {
  const message = messages.value[messages.value.length - 1];

  if (!lastMessage.value) {
    lastMessage.value = message;

    return;
  }

  if (lastMessage.value.id !== message.id) {
    newMessagesCount.value++;
    lastMessage.value = message;

    if (getChatContainerScrollBottom() <= SHOW_DOWN_BUTTON_OFFSET) {
      nextTick(() => {
        scrollToLastMessage();
      });
    }
  }
}

function openMenu(event: MouseEvent, message: MessageDto) {
  menuTop.value = event.clientY;
  menuLeft.value = event.clientX;
  selectedMessage.value = message;

  nextTick(() => {
    showMenu.value = true;
  });
}

watch(
  () => props.thread,
  () => {
    lastMessage.value = null;
    showDownButton.value = false;
    newMessagesCount.value = 0;
    initialized.value = false;
    canLoadMore.value = true;
  },
);

watch(
  messages,
  () => {
    doNotDownloadNewMessagesOnScroll.value = true;

    if (messages.value.length === 0) {
      removeLoading();

      return;
    }

    if (!initialized.value) {
      setLoading();
      removeLoading();
      scrollBottom();
      initialized.value = true;
    }

    handleNewMessage();
    doNotDownloadNewMessagesOnScroll.value = false;
  },
  { deep: true },
);

watch(scrollTop, (value, oldValue) => {
  if (doNotDownloadNewMessagesOnScroll.value) {
    return;
  }

  if (value <= 200 && oldValue > 200) {
    loadNextMessages();
  }
});

defineExpose({ scrollBottom });
</script>
<template>
  <div :class="{ 'bottom-line': showBottomLine }" class="messages-wrapper position-relative overflow-hidden h-100 px-4">
    <div ref="chatContainer" class="messages scroll-style" @scroll="handleChatScroll">
      <!-- Чаще всего ответы приходят за миллисекунды и просто мигает, чем красиво кружиться лоадер. -->
      <!--            <div class="text-center  pa-2 loader" v-if="messages === null" >-->
      <!--              <v-progress-circular indeterminate color="primary" />-->
      <!--            </div>-->
      <div ref="messagesList" class="messages-list animate-opacity">
        <chat-message
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :thread="thread"
          class="chat-message"
          @media="clickOnMediaMessage"
          @open-menu="openMenu"
        />

        <message-menu
          v-if="selectedMessage"
          v-model="showMenu"
          :left="menuLeft"
          :message="selectedMessage"
          :thread-id="thread.id"
          :top="menuTop"
        />
      </div>
      <!--      не нравиться, что текст показывается, даже если ждем ответа с вебсокета с меседжами.-->
      <div v-if="messages.length === 0 && !loading" class="d-flex justify-center align-center">
        <h5>{{ t("chats.no-messages") }}</h5>
      </div>
    </div>
    <div class="messages--bottom-actions d-flex flex-column justify-end">
      <v-btn
        v-if="showDownButton && newMessagesCount > 0"
        class="rounded-circle"
        color="primary"
        dark
        depressed="true"
        fab
        height="36px"
        size="x-small"
      >
        {{ newMessagesCount }}
      </v-btn>
      <v-btn
        v-if="showDownButton"
        class="rounded-circle mt-2"
        color="white"
        dark
        fab
        height="36px"
        size="x-small"
        @click="scrollBottom"
      >
        <v-icon color="grey" size="20"> mdi-chevron-down</v-icon>
      </v-btn>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import "@/assets/style/color";

.messages-wrapper {
  &.bottom-line:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 8px;
    right: 8px;
    border-bottom: 1px solid $grey-300;
  }

  .messages {
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
  }

  .messages--bottom-actions {
    position: absolute;
    bottom: 10px;
    right: 16px;
    z-index: 10;
  }

  .messages-list {
    .chat-message:first-child {
      margin-top: 20px;
    }
  }
}
</style>
